import {
    Audience,
    DaniConfigType,
    DataModality,
    MetricsTimePeriod,
    MetricsUserType,
    ReleaseNoteFormData,
    ReleaseNoteStatus,
} from '@/@admin/types/entities';
export const ACTION_TYPES = {
    EDIT: 'edit',
    SHOW_DANI_ACCESS: 'show-dani-access',
    SHOW_DANI_ORG_ACCESS: 'show-dani-org-access',
    VERIFY_EMAIL: 'verify-email',
    SET_TEMPORARY_PASSWORD: 'set-temporary-password',
    CHANGE_ASSOCIATED_CLIENT: 'change-associated-client',
} as const;

export const STUDIO_AI_ORG_ACCESS_LIST_QUERY_KEY = [
    'admin',
    'studio-ai-org-access',
    'list',
] as const;

export const TRIAL_MONITORING_QUERY_KEY = ['studio', 'admin', 'trial-monitoring'] as const;

export const TRIAL_AUDIT_QUERY_KEY = ['studio', 'admin', 'trial-monitoring', 'audit'] as const;

export enum SalesforceTypes {
    CLIENT = 'client',
    CONTACT = 'contact',
    OPPORTUNITY = 'opportunity',
}

export enum REPORT_TYPES {
    USERS = 'users',
    CLIENTS = 'clients',
    LICENSES = 'licenses',
    DASHBOARDS = 'dashboards',
    STUDIO_AI_CHAT_HISTORY = 'ai-chat-history',
}

/**
 * API endpoints for Dashboards data
 */
export enum APIEndpoints {
    /** Get Users for Admin Dashboard (New Portal API) */
    USERS = '/admin/users',
    /** Get Orgs for Admin Dashboard */
    ORGS = '/admin/organization',
    /** Get SF Clients */
    GET_SF_CLIENTS = '/admin/clients',
    /** Get SF Contacts */
    GET_SF_CONTACTS = '/admin/contacts',
    /** Get SF Opportunities */
    GET_SF_OPPORTUNITIES = '/admin/opportunities',
    /** Submit Deposit Funds Form */
    SUBMIT_DEPOSIT_FORM = '/admin/transactions/deposit',
    /** Submit License Form */
    CLIENT_LICENSE = '/admin/license',
    /** Get Dashboards */
    DASHBOARDS = '/admin/dashboards',
    /** Create VTracker */
    CREATE_VTRACKER = '/admin/templates/vtracker',
    /** Get Studio AI Chat History */
    STUDIO_AI_CHAT_HISTORY = '/studio-ai/admin/chat-history',
    /** Get Studio AI orgs for filter dropdowns */
    STUDIO_AI_ORGS = '/studio-ai/admin/orgs',
    /** Get Studio AI Conversation History */
    STUDIO_AI_CONVERSATION_HISTORY = '/studio-ai/conversations',
    /** Get Studio AI Metrics */
    STUDIO_AI_METRICS = '/studio-ai/admin/metrics',
    /** Trial Monitoring dashboard (CS-facing) */
    TRIAL_MONITORING = '/studio-ai/admin/trial-monitoring',
    /** Studio AI Feedback */
    STUDIO_AI_FEEDBACK = '/studio-ai/admin/feedback',
    /** Studio AI Message Markers */
    STUDIO_AI_MESSAGE_MARKERS = '/studio-ai/admin/message-markers',
    /** Studio AI User Access */
    STUDIO_AI_USER_ACCESS = '/studio-ai/admin/studio-ai-access',
    /** Studio AI Org Trial Access */
    STUDIO_AI_ORG_ACCESS = '/studio-ai/admin/studio-ai-org-access',
    /** DANI Config Admin */
    DANI_CONFIG_REVISIONS = '/studio-ai/admin/dani-config/revisions',
    DANI_CONFIG_ACTIVE = '/studio-ai/admin/dani-config/active',
    DANI_CONFIG_TEST_CONNECTION = '/studio-ai/admin/dani-config/test-connection',
    DANI_CONFIG_USER_OVERRIDES = '/studio-ai/admin/dani-config/user-overrides',
    DANI_CONFIG_ORG_OVERRIDES = '/studio-ai/admin/dani-config/org-overrides',
    /** Get User Groups */
    GET_USER_GROUPS = '/auth/user-groups',
    /** Manage User Groups */
    MANAGE_USER_GROUPS = '/auth/manage-user-groups',
    /** Get Freemium Usage Top IPs */
    FREEMIUM_USAGE_TOP_IPS = '/admin/freemium-usage/top-ips',
    /** Block/Unblock Freemium IP */
    FREEMIUM_USAGE_BLOCK = '/admin/freemium-usage/block',
    /** Blocked IPs Management */
    BLOCKED_IPS = '/admin/blocked-ips',
    /** Admin: verify user email (admin/users) */
    ADMIN_VERIFY_EMAIL = '/admin/users/verify-email',
    /** Auth: set temporary password for user (auth) */
    AUTH_SET_TEMP_PASSWORD = '/auth/admin/set-temp-password',
    /** Admin: change a user's associated client/org */
    ADMIN_CHANGE_ASSOCIATED_CLIENT = '/admin/users/change-associated-client',
    /** Admin Release Notes */
    ADMIN_RELEASE_NOTES = '/studio-ai/admin/release-notes',
    /** Internal Data Count Dashboard */
    INTERNAL_DATA_HEALTH = '/internal/data-health',
}

export const QUERY_CONFIG = {
    /** Time in milliseconds before data is considered stale (5 minutes) */
    STALE_TIME: 5 * 60 * 1000,
    /** Time in milliseconds before unused data is garbage collected (30 minutes) */
    GC_TIME: 30 * 60 * 1000,
} as const;

export const PAGE_SIZE = 100;

export const UNEDITABLE_FIELDS = {
    users: [
        'org_user_id',
        'created_timestamp',
        'last_changed_timestamp',
        'user_status',
        'count',
        'org_name',
        'has_studio_ai_access',
        'count_in_metrics',
    ],
    clients: [
        'org_id',
        'parent_org_name',
        'last_changed_timestamp',
        'created_timestamp',
        'count',
        'studio_ai_org_access',
        'studio_ai_org_access_limit',
    ],
    licenses: ['org_license_id', 'sf_account_id', 'org_name'],
    dashboards: ['template_id', 'count'],
    studioAiChatHistory: [
        'id',
        'conversationId',
        'userEmail',
        'title',
        'messageCount',
        'lastChangedTimestamp',
    ],
} as const;

export const HIDDEN_FIELDS = {
    users: ['has_studio_ai_access', 'count_in_metrics', 'count'],
    clients: ['count'],
    licenses: ['count'],
    dashboards: ['count'],
    studioAiChatHistory: ['count'],
} as const;

export const ACTION_ITEMS = {
    users: [
        ACTION_TYPES.EDIT,
        ACTION_TYPES.SHOW_DANI_ACCESS,
        ACTION_TYPES.SET_TEMPORARY_PASSWORD,
        ACTION_TYPES.VERIFY_EMAIL,
        ACTION_TYPES.CHANGE_ASSOCIATED_CLIENT,
    ],
    clients: [ACTION_TYPES.EDIT, ACTION_TYPES.SHOW_DANI_ORG_ACCESS],
    licenses: [ACTION_TYPES.EDIT],
    dashboards: [ACTION_TYPES.EDIT],
} as const;

export type ActionItemKey = (typeof ACTION_ITEMS)[keyof typeof ACTION_ITEMS][number];

export const COLUMNS = {
    users: [
        { name: 'ID', value: 'org_user_id' },
        { name: 'First Name', value: 'user_first_name' },
        { name: 'Last Name', value: 'user_last_name' },
        { name: 'Email', value: 'user_email' },
        { name: 'Org Name', value: 'org_name' },
        { name: 'Status', value: 'user_status' },
        { name: 'Auth ID', value: 'auth_user_external_key' },
        { name: 'Stripe Customer ID', value: 'external_payment_user_id' },
        { name: 'Has DANI Access', value: 'has_studio_ai_access' },
        { name: 'Show in Metrics', value: 'count_in_metrics' },
        { name: 'Created Timestamp', value: 'created_timestamp' },
        { name: 'Last Changed Timestamp', value: 'last_changed_timestamp' },
    ],
    clients: [
        { name: 'ID', value: 'org_id' },
        { name: 'Org Name', value: 'org_name' },
        { name: 'Account Id', value: 'sf_account_id' },
        { name: 'DANI Access', value: 'studio_ai_org_access' },
        { name: 'DANI Usage Limit', value: 'studio_ai_org_access_limit' },
        { name: 'Parent Account Id', value: 'sf_parent_id' },
        { name: 'Parent Account Name', value: 'parent_org_name' },
        { name: 'Contact First Name', value: 'contact_first_name' },
        { name: 'Contact Last Name', value: 'contact_last_name' },
        { name: 'Contact Email', value: 'contact_email' },
    ],
    licenses: [
        { name: 'ID', value: 'org_license_id' },
        { name: 'Client Name', value: 'org_name' },
        { name: 'Client Id', value: 'sf_account_id' },
        { name: 'Start Date', value: 'access_start_date' },
        { name: 'End Date', value: 'access_end_date' },
        { name: 'Opportunity Id', value: 'sf_opportunity_id' },
    ],
    dashboards: [
        { name: 'ID', value: 'template_id' },
        { name: 'Name', value: 'template_name' },
        { name: 'Code', value: 'template_code' },
        { name: 'URL', value: 'template_url' },
        { name: 'Description', value: 'template_desc' },
        { name: 'Icon', value: 'template_icon' },
        { name: 'Display Order', value: 'display_order' },
        { name: 'Special Title', value: 'template_special_title' },
        { name: 'Project IDs', value: 'project_ids' },
        { name: 'Is vTracker+', value: 'is_vtracker' },
        { name: 'Is Active', value: 'is_active' },
    ],
    studioAiChatHistory: [
        { name: 'Conversation ID', value: 'conversationId' },
        { name: 'User Email', value: 'userEmail' },
        { name: 'Title', value: 'title' },
        { name: 'Message Count', value: 'messageCount' },
        { name: 'Feedback Status', value: 'feedbackStatus' },
        { name: 'Last Changed', value: 'lastChangedTimestamp' },
    ],
} as const;

// Filter options for reports (boolean fields that can be filtered)
export const FILTER_OPTIONS = {
    users: [
        {
            name: 'Has DANI Access',
            value: 'has_studio_ai_access',
            type: 'boolean' as const,
        },
        {
            name: 'Show in Metrics',
            value: 'count_in_metrics',
            type: 'boolean' as const,
        },
    ],
} as const;

// Report configuration for easy expansion
export const REPORT_CONFIG = {
    users: {
        hasUpdate: true,
        defaultSort: 'org_user_id',
    },
    clients: {
        hasUpdate: true,
        defaultSort: 'org_id',
    },
    licenses: {
        hasUpdate: true,
        defaultSort: 'org_license_id',
    },
    dashboards: {
        hasUpdate: true,
        defaultSort: 'template_id',
    },
    studioAiChatHistory: {
        hasUpdate: false,
        defaultSort: 'lastChangedTimestamp',
    },
} as const;

export const METRICS_TIME_PERIOD_OPTIONS = [
    { value: MetricsTimePeriod.TODAY, label: 'Today' },
    { value: MetricsTimePeriod.ONE_WEEK, label: 'Past Week' },
    { value: MetricsTimePeriod.TWO_WEEKS, label: 'Past 2 Weeks' },
    { value: MetricsTimePeriod.ONE_MONTH, label: 'Past Month' },
    { value: MetricsTimePeriod.TWO_MONTHS, label: 'Past 2 Months' },
    { value: MetricsTimePeriod.SIX_MONTHS, label: 'Past 6 Months' },
    { value: MetricsTimePeriod.ONE_YEAR, label: 'Past Year' },
    { value: MetricsTimePeriod.ALL, label: 'All Time' },
];

export const METRICS_USER_TYPE_OPTIONS = [
    { value: MetricsUserType.ALL, label: 'All Users' },
    { value: MetricsUserType.INTERNAL, label: 'Internal (MFour)' },
    { value: MetricsUserType.FULL_USERS, label: 'Full Access' },
    { value: MetricsUserType.FREEMIUM, label: 'Freemium' },
    { value: MetricsUserType.TRIAL, label: 'Trial' },
];

export const CHAT_HISTORY_USER_TYPE_OPTIONS = [
    { value: MetricsUserType.ALL, label: 'All Users' },
    { value: MetricsUserType.INTERNAL, label: 'Internal (MFour)' },
    { value: MetricsUserType.FULL_USERS, label: 'Full Access' },
    { value: MetricsUserType.FREEMIUM, label: 'Freemium' },
    { value: MetricsUserType.TRIAL, label: 'Trial' },
];

export const BOOLEAN_FILTER_OPTIONS = [
    { key: 'true', label: 'Yes' },
    { key: 'false', label: 'No' },
];

// Human-readable names for modalities
export const MODALITY_LABELS: Record<DataModality, string> = {
    [DataModality.APP]: 'App',
    [DataModality.PURCHASE]: 'Purchase',
    [DataModality.VENUE]: 'Venue',
    [DataModality.SURVEY]: 'Survey',
    [DataModality.DEMOGRAPHICS]: 'Demographics',
    [DataModality.WEB]: 'Web',
};
// Colors for each modality
export const MODALITY_COLORS: Record<DataModality, string> = {
    [DataModality.APP]: '#3b82f6', // blue
    [DataModality.PURCHASE]: '#10b981', // emerald
    [DataModality.VENUE]: '#8b5cf6', // violet
    [DataModality.SURVEY]: '#06b6d4', // cyan
    [DataModality.DEMOGRAPHICS]: '#f59e0b', // amber
    [DataModality.WEB]: '#6b7280', // gray
};

// Chip colors for modalities
export const MODALITY_CHIP_COLORS: Record<
    DataModality,
    'primary' | 'success' | 'secondary' | 'warning' | 'danger' | 'default'
> = {
    [DataModality.APP]: 'primary',
    [DataModality.PURCHASE]: 'success',
    [DataModality.VENUE]: 'secondary',
    [DataModality.SURVEY]: 'primary',
    [DataModality.DEMOGRAPHICS]: 'warning',
    [DataModality.WEB]: 'default',
};

export const CONFIG_TYPE_LABELS: Record<DaniConfigType, string> = {
    standard: 'Standard',
    freemium: 'Freemium',
};

export const CONFIG_TYPE_COLORS: Record<DaniConfigType, 'primary' | 'secondary' | 'warning'> = {
    standard: 'primary',
    freemium: 'warning',
};

/** Regex pattern for semantic versioning (X.Y.Z) */
export const SEMVER_REGEX = /^\d+\.\d+\.\d+$/;

/** Dropdown options for the release note audience selector */
export const AUDIENCE_OPTIONS: { key: Audience; label: string }[] = [
    { key: 'all', label: 'All Users' },
    { key: 'internal', label: 'Internal Only' },
];

/** Default empty state for the release note form */
export const RELEASE_NOTE_EMPTY_FORM: ReleaseNoteFormData = {
    releaseName: '',
    productionDate: '',
    title: '',
    body: '',
    featureAreas: [],
    videoUrl: '',
    publishedAt: '',
    imageUrls: {},
};

/** HeroUI chip color mapped to each release note status */
export const RELEASE_NOTE_STATUS_COLOR: Record<
    ReleaseNoteStatus,
    'default' | 'success' | 'warning' | 'danger'
> = {
    draft: 'default',
    published: 'success',
    scheduled: 'warning',
    inactive: 'danger',
};

/** Human-readable label mapped to each release note status */
export const RELEASE_NOTE_STATUS_LABEL: Record<ReleaseNoteStatus, string> = {
    draft: 'Draft',
    published: 'Published',
    scheduled: 'Scheduled',
    inactive: 'Inactive',
};

/** Filter options for the release note status dropdown */
export const RELEASE_NOTE_STATUS_FILTER_OPTIONS = [
    { key: '', label: 'All' },
    { key: 'draft', label: 'Draft' },
    { key: 'published', label: 'Published' },
    { key: 'scheduled', label: 'Scheduled' },
];

/** Sortable column definitions for the release notes table */
export const RELEASE_NOTES_SORT_COLUMNS = [
    { name: 'Created', value: 'createdTimestamp' },
    { name: 'Release date', value: 'productionDate' },
    { name: 'Title', value: 'title' },
    { name: 'Published', value: 'publishedAt' },
];
